export interface ExtractedFieldRow {
  path: string;
  value: string;
}

const humanizeKey = (k: string): string =>
  k
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

const join = (path: string, part: string): string => (path ? `${path} › ${part}` : part);

/** A wrapped scalar is { value: <non-object> }; an object whose `value` is itself an object is a group. */
const isWrappedScalar = (o: Record<string, unknown>): boolean =>
  'value' in o && (o.value === null || typeof o.value !== 'object');

/**
 * The extraction endpoint's stored tree (objects nest, arrays are lists of wrapped items,
 * a scalar is { value }) as readable rows. Empty values are skipped.
 */
export function flattenExtractedFields(fields: Record<string, unknown> | null): ExtractedFieldRow[] {
  const out: ExtractedFieldRow[] = [];
  const walk = (node: unknown, path: string): void => {
    if (node === null || node === undefined || node === '') return;
    if (Array.isArray(node)) {
      node.forEach((item, i) => walk(item, `${path} [${i + 1}]`));
      return;
    }
    if (typeof node === 'object') {
      const o = node as Record<string, unknown>;
      if (isWrappedScalar(o)) {
        walk(o.value, path);
        return;
      }
      for (const [k, v] of Object.entries(o)) walk(v, join(path, humanizeKey(k)));
      return;
    }
    out.push({ path, value: String(node) });
  };
  walk(fields, '');
  return out;
}
